<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import SidenavList from "./SidenavList.vue";
import {
  getCurrentRoleName,
  ROLES,
  isOwner,
  isAdmin,
  isInstructor,
  isStudent,
  isGuest
} from "@/utilities/roles";
import logoWhite from "@/assets/AppLogo.png";
import logoWhite2 from "@/assets/NimbusLogo.png";

const store = useStore();
const router = useRouter();
const isRTL = computed(() => store.state.isRTL);
const layout = computed(() => store.state.layout);
const sidebarType = computed(() => store.state.sidebarType);
const isMinimized = computed(() => store.state.isMinimized);

// Get user data from sessionStorage
const userData = computed(() => store.state.userData);

const isAuthenticated = computed(() => store.state.isAuthenticated);

// Get role name
const currentRoleId = computed(() => userData.value?.roleId);
const roleName = computed(() => getCurrentRoleName(userData.value?.roleId));

// Determine icon based on role ID
const roleIcon = computed(() => {
  if (isOwner(currentRoleId.value)) return 'cogs';
  if (isAdmin(currentRoleId.value)) return 'user-tie';
  if (isInstructor(currentRoleId.value)) return 'chalkboard-teacher';
  if (isStudent(currentRoleId.value)) return 'user-graduate';
  return 'user'; // default icon
});

// Determine icon class based on role
const iconClass = computed(() => {
  return `fas fa-${roleIcon.value}`;
});

// Role-based background colors using role IDs
const iconBackground = computed(() => {
  if (isOwner(currentRoleId.value)) return 'bg-gray-600';
  if (isAdmin(currentRoleId.value)) return 'bg-warning';
  if (isInstructor(currentRoleId.value)) return 'bg-info';
  if (isGuest(currentRoleId.value)) return 'bg-blue';
  return 'bg-success'; // default for student
});

// Navigate to profile
const goToProfile = () => {
  const role = currentRoleId.value;
  if (role !== ROLES.GUEST && role !== undefined) {
    router.push('/mi-perfil');
  }
};

const goToHome = () => {
  router.push('/');
  
};

</script>

<template>
  <aside id="sidenav-main" class="sidenav navbar navbar-vertical navbar-expand-xs border-" :class="` ${sidebarType} ${isRTL ? 'fixed-end me-3 rotate-caret' : 'fixed-start'
    } ${layout === 'landing' ? 'bg-transparent shadow-none' : ''}`">
    <div class="sidenav-header d-flex align-items-center pointer" @click="goToHome"
      :class="isMinimized ? 'justify-content-center' : 'justify-content-start'">
      <img :src="logoWhite" class="m-0 navbar-brand p-1 ps-2" :style="{
        'max-height': isMinimized ? '60px' : '70px',
        'width': 'auto !important',
        'transition': 'all 0.3s ease'
      }" alt="main_logo" />
      <div class="d-flex" v-if="!isMinimized">
        <img :src="logoWhite2" class="m-0 navbar-brand p-1 ps-2" :style="{
          'max-height': isMinimized ? '60px' : '30px',
          'width': 'auto !important',
          'transition': 'all 0.3s ease',
          'filter': 'brightness(0) invert(1) drop-shadow(0 0 rgba(255,255,255,0.8))'
        }" alt="main_logo" />
      </div>
    </div>

    <hr class="mt-0 horizontal light mb-5" />

    <div class="sidenav-header">
      <div class="sidenav-header d-flex justify-content-center align-items-center">
        <div class="icon-container rounded-circle d-flex align-items-center justify-content-center hover-edit"
          :class="[iconBackground, isMinimized ? 'large-icon' : 'xlarge-icon']" @click="goToProfile">
          <i :class="iconClass" class="text-white"></i>
          <div class="edit-overlay rounded-circle d-flex align-items-center justify-content-center">
            <i class="fas fa-pencil text-white"></i>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-center mt-3">
        <span v-if="isAuthenticated" class="font-weight-bold text-white">{{ userData.firstName || 'Nombre' }}</span>
        <span class="ms-2 font-weight-bold text-white" v-if="!isMinimized && isAuthenticated">
          {{ userData.lastName || 'Apellido' }}
        </span>
      </div>

      <div class="d-flex justify-content-center" v-if="!isMinimized">
        <span class="text-sm text-white">{{ roleName }}</span>
      </div>
    </div>

    <hr v-if="isAuthenticated" class="mt-7 horizontal light" />
    <hr v-else class="mt-6 horizontal light" />

    <sidenav-list />
  </aside>
</template>

<style>
.navbar-vertical .navbar-brand>img,
.navbar-vertical .navbar-brand-img {
  max-width: 114%;
  max-height: 6rem !important;
  transition: all 0.3s ease;
}

@media (max-width: 768px) {
  .navbar-vertical.navbar-expand-xs {
    top: 46px !important;
  }
}

/* Font Awesome styling */
.fas {
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  -webkit-font-smoothing: antialiased;
  display: inline-block;
  font-style: normal;
  font-variant: normal;
  text-rendering: auto;
  line-height: 1;
}

/* Icon container styles */
.icon-container {
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  cursor: pointer;
}

/* Size classes */
.small-icon {
  width: 40px;
  height: 40px;
  font-size: 1rem;
}

.medium-icon {
  width: 50px;
  height: 50px;
  font-size: 1.25rem;
}

.large-icon {
  width: 70px;
  height: 70px;
  font-size: 1.75rem;
}

.xlarge-icon {
  width: 100px;
  height: 100px;
  font-size: 2.5rem;
}

/* Edit overlay styles */
.edit-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.hover-edit:hover .edit-overlay {
  opacity: 0.4;
}

.pointer {
  cursor: pointer;
}

.pointer:hover {
  transform: scale(1.05);
  transition: transform 0.3s ease, background-color 0.3s ease;
  background-color: rgba(255, 255, 255, 0.1);
}


</style>